/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import Logo from '@/components/Logo';
import { ProprietaryWatermark } from '@/components/security/ProprietaryWatermark';
import { toast } from 'sonner';
import { Stethoscope, Mail, Lock, Globe, KeyRound, ArrowLeft, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AVAILABLE_LANGUAGES } from '@/i18n';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const LangSwitcher = () => {
  const { i18n } = useTranslation();
  const change = (code) => { i18n.changeLanguage(code); localStorage.setItem('sc_lang', code); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="text-[11px] px-3 py-1.5 rounded-full uppercase font-semibold glass hover:bg-card flex items-center gap-1"
          data-testid="auth-lang-trigger"
        >
          <Globe className="h-3 w-3" /> {i18n.language.toUpperCase()}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[60vh] overflow-y-auto">
        {AVAILABLE_LANGUAGES.map(l => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => change(l.code)}
            data-testid={`auth-lang-${l.code}`}
            className={i18n.language === l.code ? 'font-semibold text-primary' : ''}
          >
            <span className="mr-2 text-base">{l.flag}</span>{l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const LoginPage = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [twoFAStage, setTwoFAStage] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const result = await login(email.trim(), password);
      if (result?.requires_2fa) {
        setTwoFAStage(true);
        toast.info(t('login2FAPrompt'));
        return;
      }
      toast.success(t('loginSuccess'));
      navigate(result?.category === 'paziente' ? '/telemedicine' : '/feed');
    } catch (err) {
      const msg = err.response?.data?.detail || t('invalidCredentials');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return;
    setLoading(true);
    try {
      const result = await login(email.trim(), password, otp);
      if (result?.requires_2fa) {
        toast.error(t('login2FAWrong'));
        return;
      }
      toast.success(t('loginSuccess'));
      navigate(result?.category === 'paziente' ? '/telemedicine' : '/feed');
    } catch (err) {
      const msg = err.response?.data?.detail || t('login2FAWrong');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-hero-gradient flex items-center justify-center px-4 py-8 relative isolate">
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
      <ProprietaryWatermark />
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24 }}
        className="w-full max-w-md relative"
      >
        <div className="flex items-center justify-between mb-6">
          <Logo size={44} withText textClassName="text-base" tagline={t('tagline')} />
          <LangSwitcher />
        </div>

        <Card className="p-6 rounded-2xl shadow-elev-lg">
          <AnimatePresence mode="wait">
            {!twoFAStage ? (
              <motion.div
                key="login-form-wrap"
                initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Stethoscope className="h-5 w-5 text-primary" />
                  <h1 className="font-display font-semibold text-xl">{t('welcomeBack')}</h1>
                </div>
                <p className="text-sm text-muted-foreground mb-5">{t('login')}</p>
                <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                  <div>
                    <Label htmlFor="email" className="text-xs text-muted-foreground">{t('email')}</Label>
                    <div className="mt-1 relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" data-testid="login-email-input" required />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-xs text-muted-foreground">{t('password')}</Label>
                    <div className="mt-1 relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9" data-testid="login-password-input" required />
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                    {loading ? t('loading') : t('login')}
                  </Button>
                </form>
                <div className="mt-3 text-right">
                  <Link to="/forgot-password" className="text-xs text-primary hover:underline font-semibold" data-testid="login-forgot-password-link">
                    {t('pwResetForgotLink')}
                  </Link>
                </div>
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-card px-2 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">oppure</span>
                  </div>
                </div>
                <Link
                  to="/auth/magic-link"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-primary/40 text-primary hover:bg-primary/5 text-sm font-semibold py-2 transition-colors"
                  data-testid="login-magic-link-cta"
                >
                  <Sparkles className="h-4 w-4" /> Accedi senza password (Magic Link)
                </Link>
                <div className="mt-5 text-sm text-center text-muted-foreground">
                  {t('dontHaveAccount')} <Link to="/welcome" className="text-primary font-medium hover:underline" data-testid="login-goto-register">{t('registerNow')}</Link>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="otp-form-wrap"
                initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <KeyRound className="h-5 w-5 text-primary" />
                  <h1 className="font-display font-semibold text-xl">{t('login2FATitle')}</h1>
                </div>
                <p className="text-sm text-muted-foreground mb-5">{t('login2FADesc')}</p>
                <form onSubmit={submit2FA} className="space-y-4" data-testid="login-2fa-form">
                  <div>
                    <Label htmlFor="otp" className="text-xs text-muted-foreground">{t('login2FACode')}</Label>
                    <Input
                      id="otp"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      autoFocus
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      className="text-center text-2xl font-mono tracking-[0.5em] h-14"
                      placeholder="123456"
                      data-testid="login-otp-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading || otp.length !== 6} data-testid="login-2fa-submit">
                    {loading ? t('loading') : t('login2FAVerify')}
                  </Button>
                </form>
                <button
                  onClick={() => { setTwoFAStage(false); setOtp(''); }}
                  className="mt-4 w-full text-sm text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1"
                  data-testid="login-2fa-back-btn"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> {t('back')}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>

        <div className="mt-6 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <Globe className="h-3 w-3" /> {AVAILABLE_LANGUAGES.length} lingue disponibili
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
